/**
 * Opérateurs de filtre acceptés par le moteur de requête.
 *
 * SOURCE DE VÉRITÉ : `backend/src/engine/security.ts` (`ALLOWED_OPS`). Tout
 * opérateur absent de cette liste fait échouer la requête en 400
 * (`Invalid operator: …`) — il n'existe pas d'opérateur « front only ».
 *
 * Correspondances côté connecteurs : `exact` → `EXACT` en GA4
 * (`engine/connectors/ga4.ts`) et `=` en GAQL (`engine/connectors/ads.ts`).
 * L'égalité s'écrit donc `exact` sur une dimension et `eq` sur une métrique,
 * jamais `'='`.
 */

/** Miroir exact de `ALLOWED_OPS` (backend/src/engine/security.ts). */
export const ALLOWED_FILTER_OPERATORS = [
  'exact',
  'contains',
  'begins_with',
  'regex',
  'regex_contains',
  'eq',
  'gt',
  'lt',
  'gte',
  'lte',
  'between',
  'is_null',
] as const;

export type FilterOperator = (typeof ALLOWED_FILTER_OPERATORS)[number];

export interface FilterOperatorOption {
  /** Clé synthétique du <select> (encode operator + mode). */
  value: string;
  label: string;
  operator: FilterOperator;
  mode?: 'exclude';
}

/** Opérateurs proposés sur une dimension (valeurs textuelles). */
export const DIMENSION_FILTER_OPERATORS: FilterOperatorOption[] = [
  { value: 'exact', label: 'est', operator: 'exact' },
  { value: 'not_exact', label: "n'est pas", operator: 'exact', mode: 'exclude' },
  { value: 'contains', label: 'contient', operator: 'contains' },
  { value: 'not_contains', label: 'ne contient pas', operator: 'contains', mode: 'exclude' },
  { value: 'begins_with', label: 'commence par', operator: 'begins_with' },
  { value: 'not_begins_with', label: 'ne commence pas par', operator: 'begins_with', mode: 'exclude' },
  { value: 'regex', label: 'correspond à la regex', operator: 'regex' },
  { value: 'not_regex', label: 'ne correspond pas à la regex', operator: 'regex', mode: 'exclude' },
];

/** Opérateurs proposés sur une métrique (valeurs numériques). */
export const METRIC_FILTER_OPERATORS: FilterOperatorOption[] = [
  { value: 'eq', label: 'est égal à', operator: 'eq' },
  { value: 'gt', label: 'est supérieur à', operator: 'gt' },
  { value: 'gte', label: 'est supérieur ou égal à', operator: 'gte' },
  { value: 'lt', label: 'est inférieur à', operator: 'lt' },
  { value: 'lte', label: 'est inférieur ou égal à', operator: 'lte' },
];

/** Opérateurs proposés pour un champ, selon qu'il s'agit d'une métrique ou d'une dimension. */
export function operatorsFor(isMetric: boolean): FilterOperatorOption[] {
  return isMetric ? METRIC_FILTER_OPERATORS : DIMENSION_FILTER_OPERATORS;
}

/** Opérateur par défaut d'un champ : l'égalité de son type. */
export function defaultOperatorFor(isMetric: boolean): FilterOperator {
  return operatorsFor(isMetric)[0].operator;
}

/** Un opérateur passera-t-il `sanitizeFilters` côté backend ? */
export function isAllowedOperator(operator: string): boolean {
  return (ALLOWED_FILTER_OPERATORS as readonly string[]).includes(operator);
}

const ALL_OPTIONS = () => [...DIMENSION_FILTER_OPERATORS, ...METRIC_FILTER_OPERATORS];

/** Clé synthétique du <select> pour une paire (operator, mode). */
export function operatorOptionValue(operator: string, mode?: string): string {
  const found = ALL_OPTIONS().find((o) => o.operator === operator && (o.mode ?? undefined) === (mode ?? undefined));
  return found ? found.value : operator;
}

/** Résout une clé de <select> en `{ operator, mode }`. */
export function resolveOperatorOption(value: string): { operator: FilterOperator; mode?: 'exclude' } {
  const found = ALL_OPTIONS().find((o) => o.value === value);
  return found ? { operator: found.operator, mode: found.mode } : { operator: value as FilterOperator, mode: undefined };
}
